<template>
  <ListLayout
    :title="customerAddressModule.customerAddressPlural"
    :context="context"
    :tableData="customerAddressList"
    :tableHeader="tableHeaders"
    :total="totalItems"
    @getList="getCustomerAddressList"
    ref="listTable"
  >
    <template #listAction>
      <SecondaryButton
        id="customerAddressExport"
        v-if="permissions && permissions.view"
        btnClass="mr-2 rounded-lg"
        @click="openDownloadDialog = true"
        name="Export"
        outlined
        iconClass="secondary--text mr-2"
        icon="mdi-download"
        iconSize="x-large"
      >
      </SecondaryButton>
      <v-menu offset-y>
        <template v-slot:activator="{ on: menu, attrs }">
          <v-tooltip bottom :color="isButtonDisabled ? 'error' : ''">
            <template v-slot:activator="{ on: tooltip }">
              <div v-on="{ ...tooltip }">
                <PrimaryButton
                  id="customerAddressAdd"
                  v-if="permissions && permissions.add"
                  :disabled="isButtonDisabled"
                  v-bind="attrs"
                  v-on="{ ...menu }"
                  icon="mdi-plus"
                  iconSize="x-large"
                  :name="`Add ${customerAddressModule.customerAddress}`"
                >
                </PrimaryButton>
              </div>
            </template>
            <span>{{ buttonTooltip }}</span>
          </v-tooltip>
        </template>
        <v-list dense flat>
          <v-list-item id="add-customer-address" @click="addCustomerAddress">
            <v-list-item-title>{{
              `Add ${customerAddressModule.customerAddress}`
            }}</v-list-item-title>
          </v-list-item>
          <v-list-item id="import-customer-address" @click="showBulkUploadDialog = true">
            <v-list-item-title>Import Customer Address</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
    <template #rightFilterSlot>
      <div class="d-flex align-center justify-end w-100">
        <ViewAddressLocation />

        <RefreshListButton
          id="customerAddressRefreshList"
          :isIcon="false"
          @click="$refs.listTable.searchTriggered()"
        />
      </div>
    </template>
    <template #dialogs>
      <CustomerAddressForm
        :edit-mode="isEditing"
        v-model="showCustomerAddressForm"
        :customerAddressId="selectedCustomerAddressID"
        @refreshList="
          !isEditing ? checkSubscriptionLimit() : null;
          getCustomerAddressList({ limit: 10, offset: 0 });
        "
      />
      <LogDialog
        :logsArray="customerAddressLogs"
        :title="`${customerAddressModule.customerAddress} Logs`"
        v-model="showCustomerAddressLogs"
      />
      <TagListForm
        v-model="showTagComponent"
        :parentObj="selectedCustomerAddressObject"
        tagType="Customer Address"
      />
      <DownloadSheet
        v-model="openDownloadDialog"
        :title="`Download ${customerAddressModule.customerAddress} sheet`"
        module="customerAddress"
        method="downloadCustomerAddressSheet"
      />
      <CustomerAddressLocation
        ref="CustomerAddressLocation"
        v-model="openCustomerAddressMapDialog"
      />

      <BulkUpload
        uploadTo="customer_address"
        v-model="showBulkUploadDialog"
        :hasBulkActions="true"
        @refreshList="
          checkSubscriptionLimit();
          getCustomerAddressList({ limit: 10, offset: 0 });
        "
      />
    </template>
  </ListLayout>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import { bus } from "@/main.js";
import CustomerAddressButton from "@/components/Button/CustomerAddressButton.vue";
import ListLayout from "@/components/LayoutComponents/ListLayout.vue";
import CustomerAddressForm from "@/components/ModuleBasedComponents/CustomerAddress/CustomerAddressForm.vue";
import WhatsAppNotificationIcon from "@/components/AgIcon/WhatsAppNotificationIcon.vue";
import EmailNotification from "@/components/AgIcon/EmailNotification.vue";
import LogDialog from "@/components/BaseComponents/LogDialog.vue";
import TagListForm from "@/components/GeneralComponents/TagListForm.vue";
import DownloadSheet from "@/components/GeneralComponents/DownloadSheet.vue";
import CustomerWarning from "@/components/AgIcon/CustomerWarning.vue";
import RefreshListButton from "@/components/BaseComponents/RefreshListButton.vue";
import { customerAddressModule } from "@/utils/moduleMapping";
import CustomerAddressLocation from "@/components/ModuleBasedComponents/CustomerAddress/CustomerAddressLocation.vue";
import BulkUpload from "@/components/BaseComponents/BulkUpload.vue";
import ViewAddressLocation from "@/components/ModuleBasedComponents/CustomerAddress/ViewAddressLocation.vue";

export default {
  components: {
    CustomerAddressButton,
    ListLayout,
    CustomerAddressForm,
    WhatsAppNotificationIcon,
    EmailNotification,
    LogDialog,
    TagListForm,
    DownloadSheet,
    CustomerWarning,
    RefreshListButton,
    CustomerAddressLocation,
    BulkUpload,
    ViewAddressLocation,
  },
  data() {
    return {
      customerAddressModule,
      permissions: null,
      sorting: {},
      itemsPerPage: 10,
      pageNo: 1,
      totalItems: 0,
      filters: {},
      customerAddressList: [],
      headerSelected: [
        {
          headerName: "",
          field: "warning",
          sortable: false,
          pinned: "left",
          width: 60,
          cellRenderer: "CustomerWarning",
        },
        {
          headerName: "Location ID",
          field: "location_id",
          sortable: false,
          pinned: "left",
          minWidth: 200,
        },
        {
          headerName: "Customer Code",
          field: "customer_code",
          pinned: "left",
          sortable: false,
        },
        {
          headerName: "From Time",
          field: "from_time",
          sortable: false,
        },
        {
          headerName: "To Time",
          field: "to_time",
          sortable: false,
        },
        {
          headerName: "Processing Time",
          field: "processing_time",
          sortable: false,
        },
        {
          headerName: "Address",
          field: "address",
          sortable: false,
          tooltipField: "address",
        },
      ],

      isEditing: false,
      showCustomerAddressForm: false,
      selectedCustomerAddressID: null,

      showBulkUploadDialog: false,

      customerAddressLogs: [],
      showCustomerAddressLogs: false,

      showTagComponent: false,
      selectedCustomerAddressObject: {},

      openDownloadDialog: false,

      isButtonDisabled: false,
      openCustomerAddressMapDialog: false,
      buttonTooltip: "Add Customer Addresses",
    };
  },
  computed: {
    tableHeaders() {
      return [
        ...this.headerSelected,
        {
          headerName: "Actions",
          field: "actions",
          pinned: "right",
          minWidth: 250,
          maxWidth: 250,
          sortable: false,
          width: 250,
          cellRenderer: "CustomerAddressButton",
        },
      ];
    },
    context() {
      return { parentComponent: this };
    },
    profileObject() {
      let obj = localStorage.getItem("user_data");
      return obj ? JSON.parse(obj) : null;
    },
  },
  methods: {
    getCustomerAddressMap(id) {
      this.openCustomerAddressMapDialog = true;
      let timer = setInterval(() => {
        if (this.$refs.CustomerAddressLocation) {
          clearInterval(timer);
          this.$refs.CustomerAddressLocation.getCustomerAddressMapDetails(id);
        }
      }, 100);
      setTimeout(() => {
        clearInterval(timer);
      }, 10000);
    },
    checkSubscriptionLimit() {
      this.$api.company
        .checkSubscriptionLimit(this.profileObject.company, {
          module: "customeraddress",
        })
        .then(() => {
          this.isButtonDisabled = false;
          return true;
        })
        .catch((err) => {
          bus.$emit("showToastMessage", {
            message: err.data.non_field_errors[0],
            color: "error",
            timeout: 4000,
          });
          this.isButtonDisabled = true;
          this.buttonTooltip = err.data.non_field_errors[0];
          return false;
        });
    },
    addTagsForCustomerAddress(customerAddressObj) {
      this.selectedCustomerAddressObject = customerAddressObj;
      this.showTagComponent = true;
    },
    getCustomerAddressLogs(id, customer = null) {
      bus.$emit("showLoader", true);
      let params = {
        customer: customer,
        customer_address: id,
      };
      this.$api.logs
        .getCustomerAddressLogs(params)
        .then((res) => {
          this.customerAddressLogs = res.data;
          this.showCustomerAddressLogs = true;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getCustomerAddressList(params = {}) {
      bus.$emit("showLoader", true);
      params = {
        ...params,
        ...this.filters,
      };

      if ("ordering" in params && params.ordering.length > 0) {
        params = { ...params, ordering: params.ordering.join(",") };
      }

      this.$api.customerAddress
        .getCustomerAddressList(params)
        .then((res) => {
          this.customerAddressList = res.data;
          this.totalItems = res.count;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    addCustomerAddress() {
      this.selectedCustomerAddressID = null;
      this.isEditing = false;
      this.showCustomerAddressForm = true;
    },
    editCustomerAddress(id) {
      this.selectedCustomerAddressID = id;
      this.isEditing = true;
      this.showCustomerAddressForm = true;
    },
    viewCustomerAddress(id) {
      this.$router.push(`customerAddress/details/${id}`);
    },
  },
  mounted() {},
  created() {
    this.permissions = this.$permissions.getPermission("customer");

    this.checkSubscriptionLimit();
  },
};
</script>
